















































































































































































































































































































































































































































































































































































































































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import { useCreateInvoice, useUpdateInvoice } from '@/composable/invoice';
import { AxiosError } from 'axios';

import { cloneDeep } from 'lodash';
import {
  diffDeep,
  setErrorFromAxiosError,
  convertObjectIdsToNumbers
} from '@/utils/helpers';
import {
  IInvoice,
  IInvoiceData,
  Invoice,
  PaymentPlanAmountTypeEnum,
  QuoteState
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';
import { requiredValidator } from '@/utils/validators';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import DatePicker from '@/components/common/DatePicker.vue';
import CurrencyPicker from '@/components/currency/CurrencyPicker.vue';
import CountryPicker from '@/components/common/CountryPicker.vue';
import BankPicker from '@/components/bank/PaymentInstructionPicker.vue';
import QuotePicker from '@/components/quote-common/QuotePicker.vue';
import QuillPDFEditor from '@/components/common/QuillPDFEditor.vue';

export default defineComponent({
  name: 'InvoiceDialog',
  components: {
    ALDialog,
    ErrorAlert,
    CurrencyPicker,
    DatePicker,
    CountryPicker,
    BankPicker,
    QuotePicker,
    QuillPDFEditor
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    invoice: {
      type: Object as PropType<IInvoiceData | null>,
      default: null
    }
  },
  emits: ['update:dialog'],
  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const invoiceData = ref<IInvoiceData>({
      amount_type: PaymentPlanAmountTypeEnum.FixedAmount,
      buyer_detail: { address: {} },
      shipping_detail: { address: {} },
      quotes: []
    });

    watch(
      localDialog,
      value => {
        if (value) {
          resetFormValidation();
          invoiceData.value = !props.isEdit
            ? ({
                amount_type: PaymentPlanAmountTypeEnum.FixedAmount,
                buyer_detail: { address: {} },
                shipping_detail: { address: {} },
                quotes: []
              } as IInvoiceData)
            : cloneDeep(props.invoice!);
        }
      },
      { immediate: true }
    );

    const invoiceForm = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () => `${props.isEdit ? 'Update' : 'Create'} invoice information`
    );

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const {
      isLoading: isUpdatePending,
      error: updateError,
      mutate: updateInvoice,
      reset: updateReset
    } = useUpdateInvoice();
    const {
      isLoading: isCreatePending,
      error: createError,
      mutate: createInvoice,
      reset: createReset
    } = useCreateInvoice();

    const localError = ref<ISystemError | null>(null);

    const loading = computed(() =>
      props.isEdit ? isUpdatePending.value : isCreatePending.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit ? updateError.value : createError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function clearErrors() {
      props.isEdit ? updateReset() : createReset();
      error.value = null;
    }

    function resetFormValidation(): void {
      if (invoiceForm.value) {
        return invoiceForm.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (invoiceForm.value) {
        valid = invoiceForm.value.validate();
      }
      return valid;
    }

    async function save() {
      clearErrors();
      const valid = await validateForm();

      if (valid) {
        let invoiceInstance: IInvoice | null = null;

        try {
          invoiceInstance = new Invoice(invoiceData.value);
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (invoiceInstance != null) {
          const saveAction = props.isEdit ? updateInvoice : createInvoice;

          let payload: IInvoiceData = invoiceInstance;
          if (props.isEdit) {
            payload = diffDeep(invoiceInstance, props.invoice, true);
          }

          if (payload.quotes) {
            payload.quotes = convertObjectIdsToNumbers(payload.quotes);
          }

          saveAction(payload, {
            onSuccess: () => {
              closeDialog();
            }
          });
        }
      }
    }

    return {
      PaymentPlanAmountTypeEnum,
      QuoteState,
      localDialog,
      invoiceData,
      valid,
      invoiceForm,
      subtitle,
      closeDialog,
      save,
      clearErrors,
      requiredValidator,
      loading,
      error
    };
  }
});
